import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, ChevronDown, Globe, Sparkles, AlertTriangle, TrendingUp, PlusCircle,
  DollarSign, Wallet, Shield, Lightbulb, Store, Smartphone, Building2, Car,
  Contact, Key, Users, MessageCircle, Database, Shuffle,
} from 'lucide-react';
// Desktop: tall vertical-stacked product marks.
import rentDirectLogo from '../../assets/images/Black_RentDirect_Vertical.svg';
import rentIdLogo from '../../assets/images/rentID_Vertical.svg';
import rentShieldLogo from '../../assets/images/Black_RentShield_Verticle.svg';
import rentAiLogo from '../../assets/images/Black_RentAI_Vertical.svg';
import rentSuccessLogo from '../../assets/images/Black_RentSuccess_Vertical.svg';
import rentAcademyLogo from '../../assets/images/Black_RentAcademy_Vertical.svg';
// Mobile: compact square marks (shown < 640px; see CSS visibility toggle).
import rentDirectLogoMobile from '../../assets/images/rentdirect_mark.png';
import rentIdLogoMobile from '../../assets/images/rentID_mark.png';
import rentShieldLogoMobile from '../../assets/images/rentshield_mark.png';
import rentAiLogoMobile from '../../assets/images/rentai_mark.svg';
import rentSuccessLogoMobile from '../../assets/images/rentsuccess_mark.svg';
import rentAcademyLogoMobile from '../../assets/images/rentacademy_mark.svg';

// CTA targets. "Get Started" → app.rentid.io/sign-up; "Book Demo" →
// lead form; "Contact Sales" → Google Calendar booking.
const LEAD_FORM = 'https://api.leadconnectorhq.com/widget/form/xSNa1tu0CCXyei8UJ2ew';
const CONTACT_SALES_URL = 'https://calendar.app.google/MrkgHeoJnAkWaWsq8';

// Product logos, ordered: RentDirect, RentID, RentShield, RentAI, RentSuccess,
// RentAcademy. The feature grids and the Enterprise logo row index into this.
const RENTAL_APP_FEATURES = [
  { text: 'RentDirect', logo: rentDirectLogo, logoMobile: rentDirectLogoMobile, logoText: 'Booking & Fleet Management' },
  { text: 'RentID', logo: rentIdLogo, logoMobile: rentIdLogoMobile, logoText: 'Trust & Safety Verification Tools' },
  { text: 'RentShield', logo: rentShieldLogo, logoMobile: rentShieldLogoMobile, logoText: 'Insurance & Protection Plans' },
  { text: 'RentAI', logo: rentAiLogo, logoMobile: rentAiLogoMobile, logoText: 'AI Rental Agents' },
  { text: 'RentSuccess', logo: rentSuccessLogo, logoMobile: rentSuccessLogoMobile, logoText: 'Resolution & Support Center' },
  { text: 'RentAcademy', logo: rentAcademyLogo, logoMobile: rentAcademyLogoMobile, logoText: 'Training & Education Center' },
];

// ─── Add-On options (Pro card; prices show 15% off) ────────────────────────────
const ROADSIDE_ADD_ONS = [
  { label: 'Nearest Facility', price: '$8/mo', basePrice: 8 },
  { label: 'Choose Location (10mi)', price: '$12/mo', basePrice: 12 },
  { label: 'Choose Location (20mi)', price: '$16/mo', basePrice: 16 },
];

const TOLLS_ADD_ONS = [
  { label: 'Tolls & Tickets', price: '$10/mo', basePrice: 10 },
];

// 15% Pro discount formatted as a price string (drop trailing .00).
const formatProAddOnPrice = (basePrice) => {
  const discounted = basePrice * 0.85;
  return `$${discounted % 1 === 0 ? discounted.toFixed(0) : discounted.toFixed(2)}/mo`;
};

// ─── Fleet size tiers for the Pro card ─────────────────────────────────────────
// The first tier is a flat base fee covering up to 5 vehicles (not per-vehicle);
// tiers above it are priced per additional vehicle.
const BASE_PRICE = 100;
const BASE_INCLUDED_VEHICLES = 5;

const PRO_TIERS = [
  { label: '1–5 vehicles', min: 1, max: 5, flatPrice: BASE_PRICE, custom: false },
  { label: '6–10 vehicles', min: 6, max: 10, ratePerVehicle: 15, custom: false },
  { label: '11+ vehicles', min: 11, max: Infinity, ratePerVehicle: 8, custom: false },
];

const getTier = (count) => {
  return PRO_TIERS.find((t) => count >= t.min && count <= t.max) || null;
};

// Flat $100 base covers the first 5 vehicles, then 6–10 @ $15, 11+ @ $8.
const calculateMonthlyTotal = (count) => {
  if (count <= 0) return 0;
  let total = BASE_PRICE;
  if (count > 5) total += Math.min(count - 5, 5) * 15;
  if (count > 10) total += (count - 10) * 8;
  return total;
};

// ─── Reusable bits ─────────────────────────────────────────────────────────────
const IconList = ({ items }) => (
  <ul className="vipo-pricing__iconlist">
    {items.map(({ icon: Icon, label }) => (
      <li key={label} className="vipo-pricing__iconlist-item">
        <Icon size={16} />
        {label}
      </li>
    ))}
  </ul>
);

const FeatureRow = ({ row }) => {
  const Icon = row.feature.icon;
  return (
    <div className={`vipo-pricing__frow${row.isLast ? ' vipo-pricing__frow--last' : ''}`}>
      <div className="vipo-pricing__frow-logo">
        {row.logo ? (
          <>
            <img src={row.logo} alt={row.logoText} className="vipo-pricing__frow-logo-img vipo-pricing__frow-logo-img--desktop" />
            {row.logoMobile && (
              <img src={row.logoMobile} alt={row.logoText} className="vipo-pricing__frow-logo-img vipo-pricing__frow-logo-img--mobile" />
            )}
            <span className="vipo-pricing__frow-logo-text">{row.logoText}</span>
          </>
        ) : (
          <div className="vipo-pricing__frow-globe">
            <span className="vipo-pricing__frow-icon">
              <Globe size={18} strokeWidth={2.5} />
            </span>
            <span className="vipo-pricing__frow-text">
              <span className="vipo-pricing__frow-title">{row.logoText}</span>
              {row.logoNote && <span className="vipo-pricing__frow-note">{row.logoNote}</span>}
            </span>
          </div>
        )}
      </div>
      <div className="vipo-pricing__frow-feature">
        <span className="vipo-pricing__frow-icon">
          <Icon size={18} strokeWidth={2.5} />
        </span>
        <span className="vipo-pricing__frow-text">
          <span className="vipo-pricing__frow-title">
            {row.feature.text}
            {row.feature.badge && (
              <span className="vipo-pricing__frow-badge">{row.feature.badge}</span>
            )}
          </span>
          {row.feature.note && <span className="vipo-pricing__frow-note">{row.feature.note}</span>}
        </span>
      </div>
    </div>
  );
};

const PERFECT_FOR = [
  { icon: Smartphone, label: 'Direct Rentals' },
  { icon: Building2, label: 'Traditional Rental' },
  { icon: Car, label: 'Ride Share Rental' },
  { icon: Store, label: 'Marketplace Hosts' },
  { icon: Users, label: 'Franchise Accounts' },
  { icon: Lightbulb, label: 'New Rental Operators' },
];

const UNLIMITED_BASIC = [
  { icon: Car, label: 'Vehicle Listings' },
  { icon: Contact, label: 'CRM Contacts' },
  { icon: Key, label: 'Rental Branches' },
  { icon: Users, label: 'Staff Members' },
];

const UNLIMITED_FULL = [
  ...UNLIMITED_BASIC,
  { icon: MessageCircle, label: 'Messaging' },
  { icon: Database, label: 'Data storage' },
  { icon: Shuffle, label: 'Plan Mixing' },
];

const ProFleetCalculatorInline = ({ value, onCountChange }) => {
  const [raw, setRaw] = useState(value != null ? String(value) : '');

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setRaw(val);
    onCountChange(val === '' ? null : Math.max(1, parseInt(val, 10) || 0));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={raw}
      onChange={handleChange}
      placeholder="Enter fleet size"
      className="vipo-pricing__fleet-input"
      aria-label="Number of vehicles"
    />
  );
};

// Collapsible add-on group. Pro pricing shows original struck through + 15% off.
const AddOnDropdown = ({ title, items, proDiscount }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="vipo-pricing__addon">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="vipo-pricing__addon-toggle"
        aria-expanded={open}
      >
        <span className="vipo-pricing__addon-toggle-label">
          {title}
          {proDiscount && <span className="vipo-pricing__addon-off">15% off</span>}
        </span>
        <ChevronDown
          size={16}
          className={`vipo-pricing__addon-chevron${open ? ' vipo-pricing__addon-chevron--open' : ''}`}
        />
      </button>

      <div
        className="vipo-pricing__addon-panel"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="vipo-pricing__addon-panel-inner">
          <ul className="vipo-pricing__addon-list">
            {items.map((addon, i) => (
              <li
                key={addon.label}
                className={`vipo-pricing__addon-item${i > 0 ? ' vipo-pricing__addon-item--divider' : ''}`}
              >
                <span className="vipo-pricing__addon-item-label">{addon.label}</span>
                {proDiscount ? (
                  <span className="vipo-pricing__addon-item-price">
                    <span className="vipo-pricing__addon-item-was">{addon.price}</span>
                    <span className="vipo-pricing__addon-item-now">{formatProAddOnPrice(addon.basePrice)}</span>
                  </span>
                ) : (
                  <span className="vipo-pricing__addon-item-now">{addon.price}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─── Starter card ────────────────────────────────────────────────────────────
const STARTER_FEATURE_ROWS = [
  { logo: RENTAL_APP_FEATURES[0].logo, logoMobile: RENTAL_APP_FEATURES[0].logoMobile, logoText: RENTAL_APP_FEATURES[0].logoText, feature: { text: 'Rental Website included', icon: Globe, note: 'Fully responsive booking website.' } },
  { logo: RENTAL_APP_FEATURES[1].logo, logoMobile: RENTAL_APP_FEATURES[1].logoMobile, logoText: RENTAL_APP_FEATURES[1].logoText, feature: { text: 'RentAI credits included', icon: Sparkles, note: 'Leverage Rentii AI business features.' } },
  { logo: RENTAL_APP_FEATURES[2].logo, logoMobile: RENTAL_APP_FEATURES[2].logoMobile, logoText: RENTAL_APP_FEATURES[2].logoText, feature: { text: 'Earn 80% on incident collections', icon: AlertTriangle, note: 'Profit from rental policy resolutions.' } },
  { logo: RENTAL_APP_FEATURES[3].logo, logoMobile: RENTAL_APP_FEATURES[3].logoMobile, logoText: RENTAL_APP_FEATURES[3].logoText, feature: { text: 'Ultimate business flexibility', icon: TrendingUp, note: 'Scale based on demand or seasonality.' } },
  { logo: RENTAL_APP_FEATURES[4].logo, logoMobile: RENTAL_APP_FEATURES[4].logoMobile, logoText: RENTAL_APP_FEATURES[4].logoText, feature: { text: 'Tolls & Tickets', icon: Wallet, badge: 'Add-On', note: 'Automated toll and ticket management.' } },
  { logo: RENTAL_APP_FEATURES[5].logo, logoMobile: RENTAL_APP_FEATURES[5].logoMobile, logoText: RENTAL_APP_FEATURES[5].logoText, feature: { text: 'Roadside Assistance', icon: Shield, badge: 'Add-On', note: '24/7 roadside support for your fleet.' }, isLast: true },
];

const StarterPlanCard = ({ onViewProPlan }) => (
  <div className="vipo-pricing__hcard">
    <span className="vipo-pricing__corner-badge vipo-pricing__corner-badge--dark">Start for FREE</span>

    <div className="vipo-pricing__hcard-grid">
      <div className="vipo-pricing__hcard-left">
        <h3 className="vipo-pricing__hcard-title">PAYGo</h3>
        <p className="vipo-pricing__hcard-sub">
          Always <strong>FREE</strong> to start. Only pay when vehicle is rented. <strong>ZERO</strong> onboarding fees.
        </p>

        <div className="vipo-pricing__hcard-price-row">
          <span className="vipo-pricing__hcard-price">$7</span>
          <span className="vipo-pricing__hcard-price-unit">/Rental day</span>
        </div>

        <p className="vipo-pricing__list-label">Perfect for:</p>
        <IconList items={PERFECT_FOR} />

        <p className="vipo-pricing__list-label">Unlimited:</p>
        <IconList items={UNLIMITED_BASIC} />

        <div className="vipo-pricing__hcard-ctas">
          <p className="vipo-pricing__hcard-finecta">From onboard to on-rent in less than 60 minutes.</p>
          <button
            type="button"
            onClick={onViewProPlan}
            className="vipo-pricing__cta vipo-pricing__cta--outline"
          >
            View Pro Plan
          </button>
          <Link
            to="https://app.rentid.io/sign-up"
            className="vipo-pricing__cta vipo-pricing__cta--active"
          >
            Get Started
          </Link>
        </div>
      </div>

      <div className="vipo-pricing__hcard-right">
        <p className="vipo-pricing__included-label">What's Included</p>
        <p className="vipo-pricing__included-copy">
          Access to all rental apps, embedded tools, workflows and automations.
        </p>
        <div className="vipo-pricing__fgrid">
          {STARTER_FEATURE_ROWS.map((row) => (
            <FeatureRow key={row.logoText} row={row} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Pro card ────────────────────────────────────────────────────────────────
const PRO_FEATURE_ROWS = [
  { logo: RENTAL_APP_FEATURES[0].logo, logoMobile: RENTAL_APP_FEATURES[0].logoMobile, logoText: RENTAL_APP_FEATURES[0].logoText, feature: { text: 'Unlimited RentAI credits included', icon: Sparkles, note: 'Leverage Rentii AI business features.' } },
  { logo: RENTAL_APP_FEATURES[1].logo, logoMobile: RENTAL_APP_FEATURES[1].logoMobile, logoText: RENTAL_APP_FEATURES[1].logoText, feature: { text: 'Earn 85% on incident collections', icon: AlertTriangle, note: 'Profit from rental policy resolutions.' } },
  { logo: RENTAL_APP_FEATURES[2].logo, logoMobile: RENTAL_APP_FEATURES[2].logoMobile, logoText: RENTAL_APP_FEATURES[2].logoText, feature: { text: 'Rental Backoffice', icon: TrendingUp, note: 'Automated operations, logistics & admin.' } },
  { logo: RENTAL_APP_FEATURES[3].logo, logoMobile: RENTAL_APP_FEATURES[3].logoMobile, logoText: RENTAL_APP_FEATURES[3].logoText, feature: { text: 'Create Custom Extras', icon: PlusCircle, note: 'Add custom products and services.' } },
  { logo: RENTAL_APP_FEATURES[4].logo, logoMobile: RENTAL_APP_FEATURES[4].logoMobile, logoText: RENTAL_APP_FEATURES[4].logoText, feature: { text: 'Extra Revenue Channels', icon: DollarSign, note: 'Add profit to Rentid embedded services.' } },
  { logo: RENTAL_APP_FEATURES[5].logo, logoMobile: RENTAL_APP_FEATURES[5].logoMobile, logoText: RENTAL_APP_FEATURES[5].logoText, feature: { text: 'Tolls & Tickets', icon: Wallet, badge: '10% off', note: 'Automated toll and ticket management.' } },
  { logo: null, logoText: 'Rental Website included', logoNote: 'Fully responsive booking website.', feature: { text: 'Roadside Assistance', icon: Shield, badge: '10% off', note: '24/7 roadside support for your fleet.' }, isLast: true },
];

// Two labeled groups. Each heading owns its own column of points; on mobile the
// groups stack (heading + its items) so the heading->points pairing is kept.
const FOUNDERS_GROUP_A = [
  { label: 'RentDirect Annual Plan', badge: '10% off' },
  { label: 'Embedded Tolls & Tickets', badge: '15% off' },
  { label: 'Roadside Assistance', badge: '15% off' },
  { label: 'Monthly call with Founder and CEO' },
];

const FOUNDERS_GROUP_B = [
  { label: 'Credit Volume Discounts' },
  { label: 'Assigned Account Manager' },
  { label: 'Priority Support Access' },
  { label: 'Access industry experts' },
];

const ProPlanCard = ({ count, onCountChange, onLargeFleet }) => {
  const [perfectForOpen, setPerfectForOpen] = useState(false);
  const tier = count !== null ? getTier(count) : null;

  let displayPrice = `From $${BASE_PRICE}`;
  if (count !== null) {
    displayPrice = tier === null ? 'Custom' : `$${calculateMonthlyTotal(count)}`;
  }

  // The "included" note only applies while the base fee is what's being shown —
  // once the fleet grows past the included 5, the extra vehicles are per-vehicle.
  const showIncludedNote = count === null || count <= BASE_INCLUDED_VEHICLES;

  return (
    <div className="vipo-pricing__hcard vipo-pricing__hcard--featured">
      <div className="vipo-pricing__badge-row">
        <span className="vipo-pricing__corner-badge vipo-pricing__corner-badge--dark vipo-pricing__corner-badge--static">
          Subscribe &amp; Save
        </span>
        <span className="vipo-pricing__corner-badge vipo-pricing__corner-badge--brand vipo-pricing__corner-badge--static">
          Most Popular
        </span>
      </div>

      <div className="vipo-pricing__hcard-grid">
        <div className="vipo-pricing__hcard-left">
          <h3 className="vipo-pricing__hcard-title">Subscription</h3>
          <p className="vipo-pricing__hcard-sub">
            <strong>Save Monthly.</strong> Mix &amp; Match Plans. Unlock Extra Revenue. <strong>ZERO</strong> onboarding fees.
          </p>

          <div className="vipo-pricing__hcard-price-row">
            <span className="vipo-pricing__hcard-price">{displayPrice}</span>
            <span className="vipo-pricing__hcard-price-unit">
              /mo
              {showIncludedNote && ` (first ${BASE_INCLUDED_VEHICLES} vehicles included)`}
            </span>
          </div>

          <div className="vipo-pricing__fleet-wrap">
            <ProFleetCalculatorInline value={count} onCountChange={onCountChange} />
            {count !== null && count >= 40 && (
              <button
                type="button"
                onClick={onLargeFleet}
                className="vipo-pricing__largefleet-btn"
              >
                Large Fleet? Contact Sales
              </button>
            )}
          </div>

          <div className="vipo-pricing__paygo">
            <span className="vipo-pricing__paygo-badge">Included</span>
            <p className="vipo-pricing__paygo-title">PAYGo Anytime</p>
            <p className="vipo-pricing__paygo-copy">
              Only pay when vehicle is rented. Move freely between plans anytime.
            </p>
            <div className="vipo-pricing__hcard-price-row">
              <span className="vipo-pricing__hcard-price">$7</span>
              <span className="vipo-pricing__hcard-price-unit">/Rental day</span>
            </div>
            <p className="vipo-pricing__paygo-fine">Min. 1 vehicle must be subscribed at all times.</p>
          </div>

          <div className="vipo-pricing__collapse">
            <button
              type="button"
              onClick={() => setPerfectForOpen((o) => !o)}
              className="vipo-pricing__collapse-toggle"
              aria-expanded={perfectForOpen}
            >
              <span>Perfect for:</span>
              <ChevronDown
                size={16}
                className={`vipo-pricing__collapse-chevron${perfectForOpen ? ' vipo-pricing__collapse-chevron--open' : ''}`}
              />
            </button>
            <div className="vipo-pricing__collapse-panel">
              <div
                className={`vipo-pricing__collapse-list${perfectForOpen ? ' vipo-pricing__collapse-list--open' : ''}`}
              >
                <IconList items={PERFECT_FOR} />
              </div>
              {!perfectForOpen && <div className="vipo-pricing__collapse-fade" />}
            </div>
          </div>

          <p className="vipo-pricing__list-label">Unlimited:</p>
          <IconList items={UNLIMITED_FULL} />

          <div className="vipo-pricing__hcard-ctas">
            <p className="vipo-pricing__hcard-finecta">From onboard to on-rent in less than 60 minutes.</p>
            <a
              href={LEAD_FORM}
              target="_blank"
              rel="noopener noreferrer"
              className="vipo-pricing__cta vipo-pricing__cta--outline"
            >
              Book Demo
            </a>
            <Link
              to="https://app.rentid.io/sign-up"
              className="vipo-pricing__cta vipo-pricing__cta--active"
            >
              Get Started
            </Link>
          </div>
        </div>

        <div className="vipo-pricing__hcard-right">
          <p className="vipo-pricing__included-label">
            What's Included <span className="vipo-pricing__included-plus">+</span>
          </p>
          <p className="vipo-pricing__included-copy">
            Everything included in Starter Plan plus: Access to all rental apps, embedded tools, workflows and automations.
          </p>
          <div className="vipo-pricing__fgrid">
            {PRO_FEATURE_ROWS.map((row) => (
              <FeatureRow key={row.logoText || row.feature.text} row={row} />
            ))}
          </div>

          <div className="vipo-pricing__founders">
            <div className="vipo-pricing__founders-grid">
              <div className="vipo-pricing__founders-group">
                <h4 className="vipo-pricing__founders-title">Join the Rentid Founder's Club</h4>
                {FOUNDERS_GROUP_A.map((item) => (
                  <div key={item.label} className="vipo-pricing__founders-item">
                    <span className="vipo-pricing__founders-check">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="vipo-pricing__founders-badge">{item.badge}</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="vipo-pricing__founders-group">
                <span className="vipo-pricing__founders-mark">Earn, Save &amp; Scale like a Founder</span>
                {FOUNDERS_GROUP_B.map((item) => (
                  <div key={item.label} className="vipo-pricing__founders-item">
                    <span className="vipo-pricing__founders-check">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="vipo-pricing__founders-badge">{item.badge}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Enterprise card ─────────────────────────────────────────────────────────
const ENTERPRISE_CATEGORIES = [
  { title: 'Booking & Fleet Management', copy: 'Centralized tools to manage reservations, vehicles, and fleet operations' },
  { title: 'Trust & Safety Verification Tools', copy: 'Identity verification, driver screening, and fraud prevention' },
  { title: 'Insurance & Protection Plans', copy: 'Comprehensive coverage options for vehicles and liability' },
  { title: 'AI Rental Agents', copy: 'Automated customer support and booking assistance' },
  { title: 'Resolution & Support Center', copy: 'Dedicated help desk for issue resolution and customer care' },
  { title: 'Training & Education Center', copy: 'Onboarding resources and best practices for your team' },
];

const EnterprisePlanCard = () => (
  <div className="vipo-pricing__hcard">
    <span className="vipo-pricing__corner-badge vipo-pricing__corner-badge--dark">Custom Plan</span>

    <div className="vipo-pricing__hcard-grid vipo-pricing__hcard-grid--reverse">
      <div className="vipo-pricing__hcard-right">
        <h3 className="vipo-pricing__hcard-title">Custom Pricing &amp; Rental Solutions</h3>
        <p className="vipo-pricing__ent-subtitle">Your Rental Brand &amp; Operation, Our Tech.</p>

        <div className="vipo-pricing__ent-logos">
          {RENTAL_APP_FEATURES.map((feature) => (
            <React.Fragment key={feature.text}>
              <img
                src={feature.logo}
                alt={feature.text}
                className="vipo-pricing__ent-logo vipo-pricing__ent-logo--desktop"
              />
              {feature.logoMobile && (
                <img
                  src={feature.logoMobile}
                  alt={feature.text}
                  className="vipo-pricing__ent-logo vipo-pricing__ent-logo--mobile"
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <p className="vipo-pricing__ent-copy">
          Our mobility experts build you a tailored plan using Rentid apps, tools, workflows and automations for your business.
        </p>

        <div className="vipo-pricing__ent-cats">
          {ENTERPRISE_CATEGORIES.map((cat) => (
            <div key={cat.title}>
              <p className="vipo-pricing__ent-cat-title">{cat.title}</p>
              <p className="vipo-pricing__ent-cat-copy">{cat.copy}</p>
            </div>
          ))}
        </div>

        <div className="vipo-pricing__ent-cards">
          <div className="vipo-pricing__ent-card">
            <p className="vipo-pricing__ent-card-title">Platform</p>
            <p className="vipo-pricing__ent-card-copy">
              Fully responsive operator portal and guest experience. All whitelabeled for your brand with all of the controls.
            </p>
          </div>
          <div className="vipo-pricing__ent-card">
            <p className="vipo-pricing__ent-card-title">API</p>
            <p className="vipo-pricing__ent-card-copy">
              Connect your systems with our robust API integrations. Minimum 50 vehicles.
            </p>
          </div>
        </div>
      </div>

      <div className="vipo-pricing__hcard-left">
        <h3 className="vipo-pricing__hcard-title">Enterprise</h3>
        <p className="vipo-pricing__hcard-sub">Solutions for growing fleets.</p>

        <div className="vipo-pricing__hcard-startat-wrap">
          <span className="vipo-pricing__hcard-startat">Starting at</span>
          <span className="vipo-pricing__hcard-price">25 vehicles</span>
        </div>

        <p className="vipo-pricing__list-label">Perfect for:</p>
        <IconList items={PERFECT_FOR.slice(0, 5)} />

        <p className="vipo-pricing__list-label">Unlimited:</p>
        <IconList items={UNLIMITED_FULL} />

        <a
          href={CONTACT_SALES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="vipo-pricing__cta vipo-pricing__cta--active"
        >
          Contact Sales
        </a>
      </div>
    </div>
  </div>
);

// ─── Section ─────────────────────────────────────────────────────────────────
const TIERS = ['Starter', 'Pro', 'Enterprise'];

const STARTER_CHECKS = ['No contracts', 'No commitments', 'No hidden fees', 'No CC sign up'];
const PRO_CHECKS = ['Unlimited features', 'Earn more revenue', 'Mix Plans', "Founder's Club Access"];
const ENTERPRISE_CHECKS = ['Custom Integrations', 'Volume-Based Pricing', 'Priority Support', 'Flexible Contract Terms'];

const PricingSection = () => {
  const [activeTier, setActiveTier] = useState('Starter');
  const [count, setCount] = useState(null);
  const selectPlanRef = useRef(null);

  const checks =
    activeTier === 'Enterprise' ? ENTERPRISE_CHECKS : activeTier === 'Pro' ? PRO_CHECKS : STARTER_CHECKS;

  // Switch to the Pro tier and scroll up to the "Select Plan" selector so the
  // user sees the plan toggle with the Pro card below it.
  const handleViewProPlan = () => {
    setActiveTier('Pro');
    requestAnimationFrame(() => {
      selectPlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <section className="vipo-section vipo-pricing">
      <div className="vipo-container vipo-container--wide">
        <div className="vipo-pricing__header">
          <p className="vipo-section__label">Introductory Transparent Pricing</p>
          <h2 className="vipo-rentai__title vipo-pricing__title">
            Start for free. Subscribe & <br />save. Build custom plan.
          </h2>
          <p className="vipo-pricing__intro">
            Set custom billing dates. Instant payouts. All in your control.
          </p>

          <p className="vipo-pricing__select-label" ref={selectPlanRef}>Select Plan</p>

          <div className="vipo-pricing__toggle">
            {TIERS.map((tier) => {
              const isActive = activeTier === tier;
              const shimmer = tier === 'Pro' && activeTier === 'Starter';
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() => setActiveTier(tier)}
                  className={
                    'vipo-pricing__toggle-btn'
                    + (isActive ? ' vipo-pricing__toggle-btn--active' : '')
                    + (shimmer ? ' vipo-pricing__toggle-btn--shimmer' : '')
                  }
                >
                  {tier}
                </button>
              );
            })}
          </div>

          <div className="vipo-pricing__checks">
            {checks.map((item) => (
              <span key={item} className="vipo-pricing__check-item">
                <span className="vipo-pricing__check-badge">
                  <Check size={12} strokeWidth={3} />
                </span>
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="vipo-pricing__cardwrap">
          {activeTier === 'Starter' && (
            <StarterPlanCard onViewProPlan={handleViewProPlan} />
          )}
          {activeTier === 'Pro' && (
            <ProPlanCard
              count={count}
              onCountChange={setCount}
              onLargeFleet={() => setActiveTier('Enterprise')}
            />
          )}
          {activeTier === 'Enterprise' && <EnterprisePlanCard />}
        </div>

        <p className="vipo-pricing__footnote">
          * Pay-as-you-go pricing applies per rental day per active vehicle. Pro pricing is
          billed monthly, starting at ${BASE_PRICE}/mo covering the first {BASE_INCLUDED_VEHICLES} vehicles,
          then per additional vehicle. Upgrade, downgrade, or cancel anytime.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
